import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, TrendingUp, Heart, MessageCircle, Share2, Eye } from 'lucide-react';
import { META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';
import { fmtNum, fmtDate, PLATFORM_LABEL, PLATFORM_COLOR } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;
const PLATFORM_FILTERS = [
  { value: 'all',       label: 'Alla' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
];

function engagementScore(p) {
  return (p.reactions || 0) + (p.comments || 0) + (p.shares_saves || 0);
}

function postMetrics(p) {
  const metrics = [
    { key: 'reactions',    icon: Heart,        value: p.reactions },
    { key: 'comments',     icon: MessageCircle, value: p.comments },
    { key: 'shares_saves', icon: Share2,        value: p.shares_saves },
  ];
  if (p.reach !== null && p.reach !== undefined) {
    metrics.push({ key: 'reach', icon: Eye, value: p.reach });
  }
  const present = metrics.filter(m => m.value !== null && m.value !== undefined);
  let topKey = null;
  if (present.length > 0) {
    topKey = present.reduce((a, b) => (b.value > a.value ? b : a)).key;
  }
  return { metrics: present, topKey };
}

export default function MetaStatsPostsTab({ accessToken, marketsParam }) {
  const [platform, setPlatform] = useState('all');
  const [offset, setOffset] = useState(0);
  const [sortByEngagement, setSortByEngagement] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_URL}/api/meta-stats/posts?markets=${marketsParam}&platform=${platform}&limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta inlägg.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam, platform, offset]);

  useEffect(() => { load(); }, [load]);

  const posts = data ? [...data.posts] : [];
  if (sortByEngagement) posts.sort((a, b) => engagementScore(b) - engagementScore(a));

  const total = data?.total || 0;
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <div data-testid="metastats-posts-tab">
      <div className="mstat-tab-badge-row"><MetaChannelBadge type="organic" /></div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="tab-switcher" style={{ margin: 0 }}>
          {PLATFORM_FILTERS.map(f => (
            <button
              key={f.value}
              data-testid={`metastats-posts-platform-${f.value}`}
              className={`tab-btn${platform === f.value ? ' tab-btn--active' : ''}`}
              style={{ fontSize: 12 }}
              onClick={() => { setPlatform(f.value); setOffset(0); }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          data-testid="metastats-posts-sort-engagement"
          className={`tab-btn${sortByEngagement ? ' tab-btn--active' : ''}`}
          style={{ fontSize: 12 }}
          onClick={() => setSortByEngagement(s => !s)}
        >
          <TrendingUp size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />
          Sortera efter engagemang
        </button>
      </div>

      {error && <div className="admin-notice" data-testid="metastats-posts-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Laddar inlägg...</div>
      ) : posts.length === 0 ? (
        <div className="admin-empty" data-testid="metastats-posts-empty">Inga inlägg för valda filter.</div>
      ) : (
        <>
          <div className="mstat-post-list" data-testid="metastats-posts-list">
            {posts.map(p => {
              const { metrics, topKey } = postMetrics(p);
              return (
                <div className="mstat-post-card" key={`${p.platform}-${p.id}`} data-testid={`metastats-post-${p.platform}-${p.id}`} style={{ borderLeft: `3px solid ${PLATFORM_COLOR[p.platform]}` }}>
                  <div className="mstat-post-meta">
                    <span className="mstat-platform-badge" style={{ background: `${PLATFORM_COLOR[p.platform]}18`, color: PLATFORM_COLOR[p.platform] }}>
                      {PLATFORM_LABEL[p.platform]}
                    </span>
                    <span>{META_MARKET_FLAGS[p.market] || ''} {p.market}</span>
                    <span>{fmtDate(p.date)}</span>
                    {p.type && <span className="mstat-post-type">{p.type}</span>}
                    {p.link && (
                      <a href={p.link} target="_blank" rel="noreferrer" className="mstat-post-link" data-testid={`metastats-post-link-${p.platform}-${p.id}`}>
                        Öppna <ExternalLink size={11} style={{ verticalAlign: '-1px' }} />
                      </a>
                    )}
                  </div>
                  <p className="mstat-post-text">{p.text_preview || <em>(ingen text)</em>}</p>
                  <div className="mstat-post-stats">
                    {metrics.map(m => (
                      <span
                        key={m.key}
                        className={`mstat-stat-pill mstat-stat-pill--${m.key}${m.key === topKey ? ' mstat-stat-pill--top' : ''}`}
                        data-testid={m.key === topKey ? `metastats-post-top-metric-${p.platform}-${p.id}` : undefined}
                      >
                        <m.icon size={13} />
                        <strong>{fmtNum(m.value)}</strong>
                        {m.key === topKey && <span className="mstat-top-tag">Topp</span>}
                      </span>
                    ))}
                    {p.fel_not && <span className="mstat-post-error" title={p.fel_not}>⚠ Datafel</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mstat-pagination" data-testid="metastats-posts-pagination">
            <button className="tab-btn" disabled={!hasPrev} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} data-testid="metastats-posts-prev">
              <ChevronLeft size={14} /> Föregående
            </button>
            <span style={{ fontSize: 12, opacity: 0.7 }}>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} av {total}</span>
            <button className="tab-btn" disabled={!hasNext} onClick={() => setOffset(o => o + PAGE_SIZE)} data-testid="metastats-posts-next">
              Nästa <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
